import { useEffect, useRef, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, FileText, Upload } from "lucide-react";

const TYPES = ["deed", "agreement", "receipt", "certificate"];

export default function AdminDocumentsPage() {
  const [docs, setDocs] = useState([]);
  const [plots, setPlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ plot_id: "", title: "", doc_type: "deed", url: "", storage_path: "", original_filename: "", content_type: "", size: 0, notes: "" });
  const fileRef = useRef(null);

  const load = async () => {
    const [d, p] = await Promise.all([api.get("/documents"), api.get("/plots")]);
    setDocs(d.data); setPlots(p.data);
  };
  useEffect(() => { load(); }, []);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Max file size 10 MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({
        ...s, url: "",
        storage_path: data.storage_path, original_filename: data.original_filename,
        content_type: data.content_type, size: data.size,
        title: s.title || data.original_filename.replace(/\.[^.]+$/, ""),
      }));
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.storage_path && !payload.url) { toast.error("Upload a file or provide a URL"); return; }
      await api.post("/documents", payload);
      toast.success("Document added");
      setOpen(false);
      setForm({ plot_id: "", title: "", doc_type: "deed", url: "", storage_path: "", original_filename: "", content_type: "", size: 0, notes: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="p-6 md:p-10 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3"><FileText className="text-primary" /><h1 className="heading text-3xl md:text-4xl">Documents</h1></div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-doc-btn"><Plus size={14} className="mr-2" />New document</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New document</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Plot</Label>
                <Select value={form.plot_id} onValueChange={(v) => setForm({ ...form, plot_id: v })}>
                  <SelectTrigger data-testid="doc-plot-select"><SelectValue placeholder="Select plot" /></SelectTrigger>
                  <SelectContent>{plots.map((p) => (<SelectItem key={p.id} value={p.id}>Plot {p.plot_number}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="doc-title-input" /></div>
              <div>
                <Label>Type</Label>
                <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{TYPES.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Upload file (PDF/image, ≤10 MB)</Label>
                <div className="flex gap-2 items-center mt-1">
                  <input ref={fileRef} type="file" hidden onChange={uploadFile} accept=".pdf,.png,.jpg,.jpeg,.webp" data-testid="doc-file-input" />
                  <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="doc-file-btn">
                    <Upload size={14} className="mr-2" />{uploading ? "Uploading…" : (form.original_filename || "Choose file")}
                  </Button>
                  {form.storage_path && <span className="text-xs text-muted-foreground truncate">{Math.round((form.size || 0) / 1024)} KB</span>}
                </div>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground text-center">— or —</div>
              <div><Label>External URL</Label><Input type="url" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value, storage_path: "" })} data-testid="doc-url-input" /></div>
              <DialogFooter><Button type="submit" disabled={!form.plot_id || !form.title || (!form.storage_path && !form.url)} data-testid="doc-save-btn">Save</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {docs.length === 0 && <div className="p-6 text-sm text-muted-foreground">No documents.</div>}
        {docs.map((d) => {
          const plot = plots.find((p) => p.id === d.plot_id);
          const href = d.storage_path ? `${backend}/api/files/${d.storage_path}` : d.url;
          return (
            <a key={d.id} href={href} target="_blank" rel="noreferrer" className="p-4 flex items-center gap-3 hover:bg-secondary/40" data-testid={`admin-doc-${d.id}`}>
              <FileText size={16} className="text-primary" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{d.title}</div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{d.doc_type} · Plot {plot?.plot_number || "—"}{d.storage_path ? " · uploaded" : " · link"}</div>
              </div>
              <div className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleDateString()}</div>
            </a>
          );
        })}
      </div>
    </div>
  );
}
